import { inject, Injectable } from '@angular/core';

import { App } from '@capacitor/app';
import { AlertController, Platform } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { CoreConfig } from '../../core.module';
import * as fromConfigActions from '../../store/actions/config.actions';
import * as fromDeviceActions from '../../store/actions/device.actions';
import * as fromSettingActions from '../../store/actions/setting.actions';
import * as fromWhiteLabelingActions from '../../store/actions/white-labeling.actions';
import * as fromConfigSelectors from '../../store/selectors/config.selectors';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private coreConfig = inject<CoreConfig>(CoreConfig);
  private alertController = inject(AlertController);
  private platform = inject(Platform);
  private store = inject(Store);

  private loaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private error: string | null = null;
  private refreshReferralCodeLastRun = 0;

  constructor() {
    this.store.pipe(select(fromConfigSelectors.selectLoadError)).subscribe((error) => {
      this.error = error;
    });
    this.store.pipe(select(fromConfigSelectors.selectLoaded), distinctUntilChanged()).subscribe((loaded) => {
      this.loaded.next(!!loaded);
    });

    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.tryRefresh();
      }
    });
  }

  tryRefresh() {
    const now = Date.now();
    if (now - this.refreshReferralCodeLastRun > 10000) {
      this.refreshReferralCodeLastRun = now;
      this.store.dispatch(fromSettingActions.refreshReferralCode());
    }
  }

  init() {
    return new Observable<boolean>((subscriber) => {
      let isInitial = true;
      this.loaded.subscribe(async (loaded) => {
        if (isInitial) {
          isInitial = false;
          this.store.dispatch(fromDeviceActions.loadDeviceInfo());
          this.store.dispatch(fromConfigActions.loadConfig());
          this.store.dispatch(
            fromWhiteLabelingActions.updateMainColors({
              payload: {
                primary: this.coreConfig.colors.primary,
                secondary: this.coreConfig.colors.secondary
              }
            })
          );
          this.platform.ready().then(() => {
            this.store.dispatch(
              fromDeviceActions.updateDeviceDimensions({
                payload: {
                  height: this.platform.height(),
                  width: this.platform.width()
                }
              })
            );
          });
        } else if (!loaded) {
          await this.showFailed();
        }
        subscriber.next(loaded);
      });
    });
  }

  async showFailed() {
    const alert = await this.alertController.create({
      backdropDismiss: false,
      buttons: [
        {
          handler: () => window.location.reload(),
          role: 'retry',
          text: 'Retry'
        }
      ],
      header: 'Initialization Issue',
      message: this.error ? this.error : 'No debug information available',
      subHeader: 'Failed to load the application configuration'
    });
    await alert.present();
  }
}
